import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useFireController } from '../../context/FireControllerContext';
import './Header.css';
import { FaCog, FaShieldAlt, FaFileContract, FaCookie, FaEnvelope, FaSignOutAlt, FaUser } from 'react-icons/fa';

/**
 * Header component - Top navigation bar with property selector and settings menu
 * @returns {JSX.Element} The rendered header with property selector and dropdown menu
 */
const Header = () => {
  const [showMenu, setShowMenu] = useState(false);
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const { fireControllers, selectedFireControllerId, selectFireController, formatStreetAddress } = useFireController();

  /**
   * Toggles the visibility of the settings dropdown menu
   * @returns {void}
   */
  const handleSettingsClick = () => {
    setShowMenu(!showMenu);
  };

  /**
   * Handles menu item click to navigate to specified path and close menu
   * @param {string} path - The route path to navigate to
   * @returns {void}
   */
  const handleMenuItemClick = (path) => {
    navigate(path);
    setShowMenu(false);
  };

  /**
   * Handles logout via Keycloak
   * @returns {void}
   */
  const handleLogout = () => {
    logout();
    setShowMenu(false);
  };

  /**
   * Handles Fire Controller selection change
   * @param {Event} event - The select change event
   * @returns {void}
   */
  const handleFireControllerChange = (event) => {
    const fireControllerId = event.target.value;
    selectFireController(fireControllerId);
  };

  return (
    <header className="header">
      <div className="header-logo-container">
        <h2>AI Fire Defense</h2>
      </div>

      <div className="header-bottom-row">
        <div className="property-selector">
          <select
            value={selectedFireControllerId || ''}
            onChange={handleFireControllerChange}
            disabled={fireControllers.length === 0}
          >
            {fireControllers.length === 0 ? (
              <option value="">No properties available</option>
            ) : (
              fireControllers.map(fc => (
                <option key={fc.id} value={fc.id}>
                  {formatStreetAddress(fc.address)}
                </option>
              ))
            )}
          </select>
        </div>
        
        <button className="settings-button" onClick={handleSettingsClick}>
          <FaCog className="settings-icon" />
        </button>
      </div>
      
      {showMenu && (
        <div className="menu-dropdown">
          <ul>
            <li onClick={() => handleMenuItemClick('/profile')}>
              <FaUser className="menu-icon" /> Profile
            </li>
            <li onClick={() => handleMenuItemClick('/privacy-policy')}>
              <FaShieldAlt className="menu-icon" /> Privacy Policy
            </li>
            <li onClick={() => handleMenuItemClick('/terms-and-conditions')}>
              <FaFileContract className="menu-icon" /> Terms and Conditions
            </li>
            <li onClick={() => handleMenuItemClick('/cookie-policy')}>
              <FaCookie className="menu-icon" /> Cookie Policy
            </li>
            <li onClick={() => handleMenuItemClick('/contact-us')}>
              <FaEnvelope className="menu-icon" /> Contact Us
            </li>
            <li onClick={handleLogout}>
              <FaSignOutAlt className="menu-icon" /> Logout
            </li>
          </ul>
        </div>
      )}
    </header>
  );
};

export default Header;